import React, { useState, useEffect } from "react";

import { Alert, Button, Loader, Icon } from "@partials";

import {
  AlertType,
  TabType,
  CheckboxKeyValueType,
  SEARCH_FORM_ATMS,
  FALLBACK_COORDINATES,
  GEO_COORDINATES_FROM_BROWSER,
  GEO_COORDINATES_FROM_SEARCH,
  LOAD_10_ITEMS,
} from "@constants";

import {
  getLocalization,
  fetchJSON,
  getAllCheckboxesKeyValueFromAPI,
  hasLocalStorageCoordinates,
  getLocalStorageCoordinates,
  updateURLParameters,
} from "@utilities";

import { usePagination } from "@hooks";

import { FiltersProps, IconsListProps, LocalizationProps, ResultsListType } from "../types";

import { useListenerMobile } from "./hooks/useListenerMobile";

import RowSearchAndFilters from "./layout/RowSearchAndFilters";
import RowResults from "./layout/RowResults";
import FiltersMobile from "./filters/mobile/FiltersMobile";
import FiltersDesktop from "./filters/desktop/FiltersDesktop";

import {
  AlertNoAddressFound,
  AlertShared,
  GeoLocationInfo,
  ResultsList,
  AtmMap,
  SearchFormBranchesAtms,
} from ".";

/* eslint-disable jsx-a11y/label-has-associated-control */

type AtmsSearchProps = {
  api: string;
  baseUrl: string;
  cultureCode: string;
  mapEnabled: boolean;
} & FiltersProps &
  IconsListProps &
  LocalizationProps &
  ResultsListType;

interface Pagination {
  rowsCount?: number;
  rowsOnPage?: number;
  pagesCount?: number;
  currentPage?: number;
}

// === ATMS - Search with address AUTOCOMPLETE and REVERSE GEOCODING
const AtmsSearch: React.FC<AtmsSearchProps> = ({
  filters,
  api,
  pagination,
  localization,
  cultureCode,
  baseUrl,
  pageSize,
  iconsList,
  mapEnabled,
}) => {
  usePagination({
    api,
    formElement: document.querySelector(`.${SEARCH_FORM_ATMS}`),
    cultureCode,
    fetchBodyData: { baseUrl },
    hasSearchCoordinates: true,
  });

  // Icons
  const { listedItems, map } = iconsList;
  const [isToggled, setToggled] = useState(false);

  // checkboxes
  const checkboxesDefaultStateObject = getAllCheckboxesKeyValueFromAPI(filters);
  const [checkboxesStateObject, setCheckboxesStateObject] = useState(checkboxesDefaultStateObject);

  // payload
  const [payloadDataResults, setPayloadDataResults] = useState<any>([]);

  // pagination
  const [resultsCurrentPage, setResultsCurrentPage] = useState(pagination.currentPage);
  const [resultsPagesCount, setResultsPagesCount] = useState(pagination.pagesCount);

  // loading
  const [isLoadingNew, setIsLoadingNew] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  // results states
  const [canShowNoResultsAlert, setCanShowNoResultsAlert] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  // search
  const [inputValue, setInputValue] = useState<string>("");
  const [showAlertNoAddressFound, setShowAlertNoAddressFound] = useState<boolean>(false);

  // TABS
  const selectedTabType = TabType.Atms;

  // ===== SEARCH - SECTION
  // RESULTS
  const hasResults = payloadDataResults.length > 0;

  const showLoader = isLoadingNew;
  const showAlertError = hasError;
  const showGeolocation = hasResults;
  const showAlertNoResults = canShowNoResultsAlert && !hasResults;
  const showResultsList = !isLoadingNew && hasResults;

  const showLoadMoreResults =
    !isLoadingNew && hasResults && resultsCurrentPage !== resultsPagesCount;

  // LOCAL STORAGE
  const hasBrowserCoordinates = hasLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER);
  const hasSearchCoordinates = hasLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH);

  // ===== CHECKBOXES - SECTION
  // MOBILE - dropdown toggle click
  useListenerMobile(selectedTabType);

  // FILTERS - load NEW data - Fallback
  const loadNewFiltersWithFallbackCoordinates = async (
    checkboxKeyValue: CheckboxKeyValueType,
    limit?: number
  ): Promise<void> => {
    setIsLoadingNew(true);
    setCanShowNoResultsAlert(false);

    try {
      const response = await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            latitude: FALLBACK_COORDINATES.lat,
            longitude: FALLBACK_COORDINATES.lng,
            baseUrl,
            limit: limit ?? pageSize ?? LOAD_10_ITEMS,
            offset: 0,
            cultureCode,
            isPageDetail: false,
            ...checkboxKeyValue,
          }),
        },
      });

      // results data from BE
      setPayloadDataResults(response.payload.data);

      // data from pagination
      const paginationData: Pagination = response.payload.pagination;
      const { currentPage, pagesCount } = paginationData;

      setResultsCurrentPage(currentPage);
      setResultsPagesCount(pagesCount);
    } catch (error) {
      setIsLoadingNew(false);
      setHasError(true);
      console.error(error);
    } finally {
      setIsLoadingNew(false);
      setCanShowNoResultsAlert(true);
    }
  };

  // FILTERS - load NEW data - Browser
  const loadNewFiltersWithBrowserCoordinates = async (
    checkboxKeyValue: CheckboxKeyValueType,
    limit?: number
  ): Promise<void> => {
    setIsLoadingNew(true);
    setCanShowNoResultsAlert(false);

    try {
      const response = await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lng,
            baseUrl,
            limit: limit ?? pageSize ?? LOAD_10_ITEMS,
            offset: 0,
            cultureCode,
            isPageDetail: false,
            ...checkboxKeyValue,
          }),
        },
      });

      // results data from BE
      setPayloadDataResults(response.payload.data);

      // data from pagination
      const paginationData: Pagination = response.payload.pagination;
      const { currentPage, pagesCount } = paginationData;

      setResultsCurrentPage(currentPage);
      setResultsPagesCount(pagesCount);
    } catch (error) {
      setIsLoadingNew(false);
      setHasError(true);
      console.error(error);
    } finally {
      setIsLoadingNew(false);
      setCanShowNoResultsAlert(true);
    }
  };

  // FILTERS - load NEW data - Search
  const loadNewFiltersWithSearchCoordinates = async (
    checkboxKeyValue: CheckboxKeyValueType,
    limit?: number
  ): Promise<void> => {
    setIsLoadingNew(true);
    setCanShowNoResultsAlert(false);

    try {
      const response = await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lng,
            baseUrl,
            limit: limit ?? pageSize ?? LOAD_10_ITEMS,
            offset: 0,
            cultureCode,
            isPageDetail: false,
            ...checkboxKeyValue,
          }),
        },
      });

      // results data from BE
      setPayloadDataResults(response.payload.data);

      // data from pagination
      const paginationData: Pagination = response.payload.pagination;
      const { currentPage, pagesCount } = paginationData;

      setResultsCurrentPage(currentPage);
      setResultsPagesCount(pagesCount);
    } catch (error) {
      setIsLoadingNew(false);
      setHasError(true);
      console.error(error);
    } finally {
      setIsLoadingNew(false);
      setCanShowNoResultsAlert(true);
    }
  };

  // FILTERS - load MORE data - Fallback
  const loadMoreFiltersWithFallbackCoordinates = async (
    checkboxKeyValue: CheckboxKeyValueType,
    limit?: number
  ): Promise<void> => {
    setIsLoadingMore(true);
    setCanShowNoResultsAlert(false);

    try {
      const response = await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            latitude: FALLBACK_COORDINATES.lat,
            longitude: FALLBACK_COORDINATES.lng,
            baseUrl,
            limit: limit ?? pagination.rowsOnPage, // limit = how many results should BE return in response
            offset: payloadDataResults.length,
            cultureCode,
            isPageDetail: false,
            ...checkboxKeyValue,
          }),
        },
      });

      // results data from BE
      const prevArray = payloadDataResults;
      const payloadArray = response.payload.data;
      const newResultsArray = prevArray.concat(payloadArray);

      setPayloadDataResults(newResultsArray);

      // data from pagination
      const paginationData: Pagination = response.payload.pagination;
      const { currentPage, pagesCount } = paginationData;

      setResultsCurrentPage(currentPage);
      setResultsPagesCount(pagesCount);
    } catch (error) {
      setIsLoadingMore(false);
      setHasError(true);
      console.error(error);
    } finally {
      setIsLoadingMore(false);
      setCanShowNoResultsAlert(true);
    }
  };

  // FILTERS - load MORE data - Browser
  const loadMoreFiltersWithBrowserCoordinates = async (
    checkboxKeyValue: CheckboxKeyValueType,
    limit?: number
  ): Promise<void> => {
    setIsLoadingMore(true);
    setCanShowNoResultsAlert(false);

    try {
      const response = await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_BROWSER).lng,
            baseUrl,
            limit: limit ?? pagination.rowsOnPage, // limit = how many results should BE return in response
            offset: payloadDataResults.length,
            cultureCode,
            isPageDetail: false,
            ...checkboxKeyValue,
          }),
        },
      });

      // results data from BE
      const prevArray = payloadDataResults;
      const payloadArray = response.payload.data;
      const newResultsArray = prevArray.concat(payloadArray);

      setPayloadDataResults(newResultsArray);

      // data from pagination
      const paginationData: Pagination = response.payload.pagination;
      const { currentPage, pagesCount } = paginationData;

      setResultsCurrentPage(currentPage);
      setResultsPagesCount(pagesCount);
    } catch (error) {
      setIsLoadingMore(false);
      setHasError(true);
      console.error(error);
    } finally {
      setIsLoadingMore(false);
      setCanShowNoResultsAlert(true);
    }
  };

  // FILTERS - load MORE data - Search
  const loadMoreFiltersWithSearchCoordinates = async (
    checkboxKeyValue: CheckboxKeyValueType,
    limit?: number
  ): Promise<void> => {
    setIsLoadingMore(true);
    setCanShowNoResultsAlert(false);

    try {
      const response = await fetchJSON({
        settings: {
          method: "POST",
          url: api,
          body: JSON.stringify({
            latitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lat,
            longitude: getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lng,
            baseUrl,
            limit: limit ?? pagination.rowsOnPage, // limit = how many results should BE return in response
            offset: payloadDataResults.length,
            cultureCode,
            isPageDetail: false,
            ...checkboxKeyValue,
          }),
        },
      });

      // results data from BE
      const prevArray = payloadDataResults;
      const payloadArray = response.payload.data;
      const newResultsArray = prevArray.concat(payloadArray);

      setPayloadDataResults(newResultsArray);

      // data from pagination
      const paginationData: Pagination = response.payload.pagination;
      const { currentPage, pagesCount } = paginationData;

      setResultsCurrentPage(currentPage);
      setResultsPagesCount(pagesCount);
    } catch (error) {
      setIsLoadingMore(false);
      setHasError(true);
      console.error(error);
    } finally {
      setIsLoadingMore(false);
      setCanShowNoResultsAlert(true);
    }
  };

  // HANDLE CHECKBOX ONCHANGE
  const handleCheckboxOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    const checkboxKey = value;
    const checkboxValue = checked;

    // handle selected checkbox
    const newCheckboxesStateObject = {
      ...checkboxesStateObject,
      [checkboxKey]: checkboxValue, // selected checkbox Key Value pair
    };
    setCheckboxesStateObject(newCheckboxesStateObject);

    // handle URL parameters
    updateURLParameters(checkboxKey, checkboxValue);
    const limit = isToggled ? 9999 : pageSize ?? LOAD_10_ITEMS;

    // LOAD NEW - data - filtered from BE
    if (hasBrowserCoordinates && !hasSearchCoordinates) {
      loadNewFiltersWithBrowserCoordinates(newCheckboxesStateObject, limit);
    }

    if (hasSearchCoordinates) {
      loadNewFiltersWithSearchCoordinates(newCheckboxesStateObject, limit);
    }

    if (!hasBrowserCoordinates && !hasSearchCoordinates) {
      loadNewFiltersWithFallbackCoordinates(newCheckboxesStateObject, limit);
    }
  };

  const loadNewList = () => {
    if (hasBrowserCoordinates && !hasSearchCoordinates) {
      loadNewFiltersWithBrowserCoordinates(checkboxesStateObject);
    }
    if (hasSearchCoordinates) {
      loadNewFiltersWithSearchCoordinates(checkboxesStateObject);
    }

    if (!hasBrowserCoordinates && !hasSearchCoordinates) {
      loadNewFiltersWithFallbackCoordinates(checkboxesStateObject);
    }
  };

  // LOAD NEW - data - when page finished loading
  useEffect(() => {
    loadNewList();
  }, [hasSearchCoordinates, hasBrowserCoordinates]);

  // LOAD MORE function to show more results
  const loadMoreResults = (limit) => {
    if (hasBrowserCoordinates && !hasSearchCoordinates) {
      return loadMoreFiltersWithBrowserCoordinates(checkboxesStateObject, limit);
    }
    if (hasSearchCoordinates) {
      return loadMoreFiltersWithSearchCoordinates(checkboxesStateObject, limit);
    }

    return loadMoreFiltersWithFallbackCoordinates(checkboxesStateObject, limit);
  };

  const handleToggle = () => {
    if (isToggled) {
      setToggled(false);
      loadNewList();
    } else {
      setToggled(true);
      pagination.rowsOnPage = 9999;
      pageSize = 9998;
      loadMoreResults(9999);
    }
  };

  return (
    <>
      <div className="container">
        <RowSearchAndFilters>
          <SearchFormBranchesAtms
            inputValue={inputValue}
            setInputValue={setInputValue}
            setShowAlertNoResult={setShowAlertNoAddressFound}
            localization={localization}
            type={selectedTabType}
            isToggled={isToggled}
          />

          <FiltersMobile
            filters={filters}
            iconsList={iconsList}
            localization={localization}
            type={selectedTabType}
            handleOnChange={handleCheckboxOnChange}
          />
          <FiltersDesktop
            filters={filters}
            iconsList={iconsList}
            type={selectedTabType}
            handleOnChange={handleCheckboxOnChange}
          />
        </RowSearchAndFilters>

        {showAlertNoAddressFound && (
          <AlertNoAddressFound
            iconsList={iconsList}
            inputValue={inputValue}
            localization={localization}
            alertSource="Atm-Search-NoAddress"
          />
        )}
      </div>

      <div className="container mb-12" id="ResultsList">
        <div className="row">
          <div className="mb-6 sm:col-22 sm:offset-1">
            {showGeolocation && (
              <GeoLocationInfo
                type={selectedTabType}
                localization={localization}
                iconsList={iconsList}
              />
            )}

            <button
              type="button"
              onClick={handleToggle}
              data-testid={isToggled ? "display-atm-list" : "display-atm-map"}
              className={`${mapEnabled ? "underline" : "hidden underline"} flex items-center`}
            >
              {isToggled ? (
                <Icon
                  name={listedItems.name}
                  viewBox={listedItems.viewbox}
                  alt={listedItems.alt}
                  svg={listedItems.svg}
                  className="icon mr-2 inline h-6 w-6 fill-red-corporate"
                />
              ) : (
                <Icon
                  name={map.name}
                  viewBox={map.viewbox}
                  alt={map.alt}
                  svg={map.svg}
                  className="icon mr-2 inline h-6 w-6 fill-red-corporate"
                />
              )}
              {isToggled
                ? getLocalization(localization, "Branches_Atms_ShowList")
                : getLocalization(localization, "Branches_Atms_ShowMap")}
            </button>
          </div>
        </div>

        {isToggled ? (
          <>
            {showAlertNoResults && (
              <div className="row">
                <div className="sm:col-22 sm:offset-1 mb-6">
                  <Alert
                    type={AlertType.Error}
                    text={getLocalization(localization, "Branches_SearchNoResults")}
                    textAttributes={{ role: "alert", "aria-live": "assertive" }}
                    alertSource="Atm-Search-NoResult"
                  />
                </div>
              </div>
            )}
            <div className="row">
              <div className="sm:col-22 sm:offset-1">
                <AtmMap
                  baseUrl={baseUrl}
                  type={selectedTabType}
                  localization={localization}
                  cultureCode={cultureCode}
                  lat={getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lat}
                  lng={getLocalStorageCoordinates(GEO_COORDINATES_FROM_SEARCH).lng}
                  showGeolocation
                  items={payloadDataResults}
                  TabType
                  isToggled
                />
              </div>
            </div>
          </>
        ) : (
          <RowResults>
            {showLoader && (
              <div className={`search-ba-loader ${hasResults ? "mt-4" : ""}`}>
                <Loader />
              </div>
            )}

            {showAlertError && (
              <AlertShared
                localization={localization}
                text="Branches_ErrorMessage"
                alertSource="Atm-Search-Error"
              />
            )}

            {showAlertNoResults && (
              <Alert
                type={AlertType.Error}
                text={getLocalization(localization, "Branches_SearchNoResults")}
                textAttributes={{ role: "alert", "aria-live": "assertive" }}
                alertSource="Atm-Search-NoResult"
              />
            )}

            {showResultsList && (
              <ResultsList
                type={selectedTabType}
                localization={localization}
                items={payloadDataResults}
                iconsList={iconsList}
              />
            )}

            {showLoadMoreResults && (
              <Button
                template="primary"
                title={getLocalization(localization, "LoadMore")}
                type="button"
                onClick={() => loadMoreResults(pagination.rowsOnPage)}
                dataTestId={`button-load-more-${selectedTabType}`}
                isLoading={isLoadingMore}
              />
            )}
          </RowResults>
        )}
      </div>
    </>
  );
};

export default AtmsSearch;
